<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div v-if="loading" class="flex-1 flex items-center justify-center py-20">
      <div class="w-10 h-10 border-4 border-[#b99a52] border-t-transparent rounded-full animate-spin"></div>
    </div>

    <div v-else-if="!provider" class="flex-1 flex items-center justify-center py-20 text-gray-400">
      Prestador não encontrado.
    </div>

    <div v-else class="max-w-4xl mx-auto w-full px-4 py-8">
      <!-- Botão voltar -->
      <div class="flex items-center gap-3 mb-4">
        <button
          @click="goBack"
          class="flex items-center gap-1 text-sm text-[#152a34] hover:text-[#b99a52] transition font-medium"
        >
          <span class="mdi mdi-arrow-left text-lg"></span>
          Voltar
        </button>
      </div>

      <!-- Cabeçalho do perfil -->
      <div class="bg-white rounded-2xl shadow border border-[#e0dcd6] p-6 mb-6">
        <div class="flex flex-col md:flex-row items-start gap-5">
          <img
            :src="avatarUrl"
            :alt="provider.name"
            class="w-24 h-24 rounded-full object-cover border-4 border-[#b99a52] flex-shrink-0"
          />
          <div class="flex-1">
            <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div>
                <h1 class="text-2xl font-bold text-[#152a34]">{{ provider.name }}</h1>
                <p v-if="provider.headline" class="text-gray-500 text-sm mt-1">{{ provider.headline }}</p>
                <div class="flex items-center gap-3 mt-2 flex-wrap">
                  <div class="flex items-center gap-1">
                    <span class="text-yellow-400">★</span>
                    <span class="font-medium text-sm">{{ ratingDisplay }}</span>
                    <span class="text-gray-400 text-xs">({{ provider.reviews_count || 0 }} avaliações)</span>
                  </div>
                  <span v-if="provider.city" class="text-xs text-gray-400 flex items-center gap-1">
                    <span class="mdi mdi-map-marker"></span>
                    {{ provider.city }}<span v-if="provider.state">/{{ provider.state }}</span>
                  </span>
                  <span v-if="provider.projects_completed > 0" class="text-xs text-gray-400">
                    {{ provider.projects_completed }} serviço(s)
                  </span>
                </div>
              </div>
            </div>
            <p v-if="provider.bio" class="text-gray-600 text-sm mt-3 leading-relaxed">{{ provider.bio }}</p>
            <!-- CTA rápido de agendamento (só aparece se há serviços) -->
            <div v-if="provider.services && provider.services.length > 0" class="mt-4 flex flex-wrap gap-2">
              <a
                href="#services"
                @click.prevent="activeTab = 'services'"
                class="inline-flex items-center gap-2 px-4 py-2 bg-[#b99a52] text-white rounded-xl text-sm font-semibold hover:bg-[#a08840] transition"
              >
                <span class="mdi mdi-calendar-check"></span>
                Ver serviços e agendar
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="flex gap-2 mb-6">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="activeTab = tab.key"
          :class="[
            'px-5 py-2 rounded-xl text-sm font-medium transition',
            activeTab === tab.key
              ? 'bg-[#152a34] text-white'
              : 'bg-white border border-gray-200 text-gray-600 hover:border-[#b99a52]'
          ]"
        >{{ tab.label }}</button>
      </div>

      <!-- Tab: Serviços -->
      <div v-if="activeTab === 'services'">
        <div v-if="provider.services && provider.services.length === 0" class="text-center py-12">
          <span class="mdi mdi-briefcase-off text-5xl text-gray-300 block mb-3"></span>
          <p class="text-gray-500 font-medium mb-1">Este prestador ainda não tem serviços disponíveis</p>
          <p class="text-gray-400 text-sm mb-5">Ele pode estar finalizando o cadastro. Tente buscar outro profissional.</p>
          <button
            @click="goBack"
            class="inline-flex items-center gap-2 px-5 py-2.5 bg-[#b99a52] text-white rounded-xl font-semibold hover:bg-[#a08840] transition text-sm"
          >
            <span class="mdi mdi-magnify"></span>
            Buscar outro profissional
          </button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="service in provider.services"
            :key="service.id"
            class="bg-white rounded-2xl shadow border border-[#e0dcd6] p-5 flex flex-col"
          >
            <div class="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 class="font-semibold text-[#152a34]">{{ service.name }}</h3>
                <p v-if="service.category" class="text-xs text-[#b99a52] mt-0.5">{{ service.category.description }}</p>
              </div>
              <div class="text-right flex-shrink-0">
                <p class="text-lg font-bold text-[#152a34]">R$ {{ formatPrice(service.price_with_fee) }}</p>
                <p class="text-xs text-gray-400">{{ service.duration_minutes }} min</p>
              </div>
            </div>
            <p v-if="service.description" class="text-sm text-gray-500 flex-1 mb-4">{{ service.description }}</p>
            <router-link
              :to="{ name: 'booking', params: { hash: provider.hash, serviceId: service.id } }"
              class="block text-center py-2 bg-[#b99a52] text-white rounded-xl text-sm font-semibold hover:bg-[#a08840] transition"
            >
              Agendar
            </router-link>
          </div>
        </div>
      </div>

      <!-- Tab: Avaliações -->
      <div v-if="activeTab === 'reviews'">
        <div v-if="!provider.reviews || provider.reviews.length === 0" class="text-center py-12 text-gray-400">
          Nenhuma avaliação ainda.
        </div>
        <div class="space-y-4">
          <div
            v-for="review in provider.reviews"
            :key="review.id"
            class="bg-white rounded-2xl shadow border border-[#e0dcd6] p-5"
          >
            <div class="flex items-center gap-3 mb-3">
              <img
                :src="reviewerAvatar(review.reviewer)"
                class="w-9 h-9 rounded-full object-cover border border-gray-200"
              />
              <div>
                <p class="font-medium text-sm">{{ review.reviewer?.name || 'Usuário' }}</p>
                <div class="flex text-yellow-400 text-xs">
                  <span v-for="i in 5" :key="i">{{ i <= review.rating ? '★' : '☆' }}</span>
                </div>
              </div>
            </div>
            <p v-if="review.comment" class="text-sm text-gray-600">{{ review.comment }}</p>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getProviderProfile } from '@/services/api'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const route = useRoute()
const router = useRouter()
const provider = ref(null)
const loading = ref(true)
const activeTab = ref('services')
const tabs = [
  { key: 'services', label: 'Serviços' },
  { key: 'reviews', label: 'Avaliações' }
]

const storageUrl = process.env.VUE_APP_URL_API_STORAGE || ''

const avatarUrl = computed(() => {
  if (!provider.value?.avatar) return 'https://ui-avatars.com/api/?name=' + encodeURIComponent(provider.value?.name || 'P')
  if (provider.value.avatar.startsWith('http')) return provider.value.avatar
  return `${storageUrl}/${provider.value.avatar}`
})

const ratingDisplay = computed(() => {
  const r = parseFloat(provider.value?.rating_average || 0)
  return r > 0 ? r.toFixed(1) : 'Novo'
})

function goBack () {
  if (window.history.length > 1) {
    router.back()
  } else {
    router.push({ name: 'provider-search' })
  }
}

function reviewerAvatar (reviewer) {
  if (!reviewer?.avatar) return 'https://ui-avatars.com/api/?name=' + encodeURIComponent(reviewer?.name || 'U')
  if (reviewer.avatar.startsWith('http')) return reviewer.avatar
  return `${storageUrl}/${reviewer.avatar}`
}

function formatPrice (price) {
  return Number(price).toFixed(2).replace('.', ',')
}

onMounted(async () => {
  try {
    const { data } = await getProviderProfile(route.params.hash)
    provider.value = data.data
  } catch (err) {
    console.error('Erro ao carregar perfil:', err)
  } finally {
    loading.value = false
  }
})
</script>
